import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Plus, Send, Trash2, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { filtrerClients, adressePreferee } from "../lib/rechercheClient";
import { catNoOf } from "../lib/localize";
import { FadeUp } from "../components/motion";
import { SEO } from "../components/SEO";
import { formatPhone } from "../lib/utils";  // format auto (123) 456-7890 (règle 2026-10-22)
import { SmsEtapesBox } from "../components/SmsEtapesBox";  // case « étapes par texto » + code (2026-10-30)
import { AddressAutocomplete } from "../components/AddressAutocomplete";  // adresse : CP d'abord + autocomplétion (2026-11-03)

const emptyItem = () => ({ catalogue: "", product_name: "", product_no: "", qty: 1, with_text: false, details: "", logos: [] });

const inputCls = "w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none transition-colors duration-300";
const labelCls = "block text-xs uppercase tracking-widest text-ivory/60 mb-1.5";

// Provinces et territoires du Canada — bande déroulante OBLIGATOIRE de la
// soumission 30 s (demande du propriétaire, 2026-10-29) : plus de saisie libre
const PROVINCES = [
  { v: "QC", fr: "Québec", en: "Quebec" },
  { v: "ON", fr: "Ontario", en: "Ontario" },
  { v: "BC", fr: "Colombie-Britannique", en: "British Columbia" },
  { v: "AB", fr: "Alberta", en: "Alberta" },
  { v: "MB", fr: "Manitoba", en: "Manitoba" },
  { v: "SK", fr: "Saskatchewan", en: "Saskatchewan" },
  { v: "NS", fr: "Nouvelle-Écosse", en: "Nova Scotia" },
  { v: "NB", fr: "Nouveau-Brunswick", en: "New Brunswick" },
  { v: "PE", fr: "Île-du-Prince-Édouard", en: "Prince Edward Island" },
  { v: "NL", fr: "Terre-Neuve-et-Labrador", en: "Newfoundland and Labrador" },
  { v: "NT", fr: "Territoires du Nord-Ouest", en: "Northwest Territories" },
  { v: "YT", fr: "Yukon", en: "Yukon" },
  { v: "NU", fr: "Nunavut", en: "Nunavut" },
];

// Page « Votre soumission en 30 secondes » (/soumission-rapide) — volontairement
// ABSENTE des onglets du haut : on y arrive par le bouton doré flottant présent
// sur chaque page. Coordonnées + date de livraison + choix catalogue/page/
// produit/quantité (3 par défaut, « + » pour en ajouter) → courriel à
// commande@trophee.ca + copie conservée dans l'admin.
export default function QuickQuote() {
  const { t, ts, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ first_name: "", last_name: "", organization: "", address1: "", city: "", province: "", postal_code: "", phone: "", email: "", needed_by: "",
    bill_address1: "", bill_city: "", bill_postal_code: "", ship_address1: "", ship_city: "", ship_postal_code: "" });
  // Case « recevoir les étapes de ma commande par texto » (2026-10-30) —
  // cochée = consentement ; un code à 6 chiffres doit ensuite être confirmé
  const [prefSmsEtapes, setPrefSmsEtapes] = useState(false);
  // Confirmation du contact BLOQUANTE (demande du propriétaire, 2026-11-03) :
  // « un des 2 doit être confirmé » — le cellulaire (texto) OU le courriel, par
  // code à 6 chiffres, AVANT l'envoi. viaContact: "texto" | "courriel".
  const [contactConfirm, setContactConfirm] = useState({ etape: "idle", via: "texto", code: "", busy: false });
  const envoyerCodeContact = async (via) => {
    setContactConfirm((c) => ({ ...c, busy: true, via }));
    try {
      await api.post("/contact/send-code", { email: form.email, phone: form.phone, via });
      setContactConfirm((c) => ({ ...c, etape: "envoye", busy: false }));
      toast.success(via === "courriel"
        ? (lang === "fr" ? "Code envoyé par courriel — regardez votre boîte." : "Code sent by email — check your inbox.")
        : (lang === "fr" ? "Code envoyé par texto — regardez votre cellulaire." : "Code sent by text — check your phone."));
    } catch (err) {
      setContactConfirm((c) => ({ ...c, busy: false }));
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  const verifierCodeContact = async () => {
    setContactConfirm((c) => ({ ...c, busy: true }));
    try {
      await api.post("/contact/verify-code", { email: form.email, phone: form.phone, code: contactConfirm.code });
      setContactConfirm((c) => ({ ...c, etape: "confirme", busy: false }));
      toast.success(lang === "fr" ? "Contact confirmé — vous pouvez envoyer." : "Contact confirmed — you can send.");
    } catch (err) {
      setContactConfirm((c) => ({ ...c, busy: false }));
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  // Cases « même adresse que le dossier client » pour Facturé à / Livrer à
  // (demande du propriétaire, 2026-10-17) — cochées par défaut : rien à retaper
  const [sameBill, setSameBill] = useState(true);
  const [sameShip, setSameShip] = useState(true);
  const [items, setItems] = useState([emptyItem(), emptyItem(), emptyItem()]);
  const [loading, setLoading] = useState(false);
  const [params] = useSearchParams();
  // Arrivée depuis une fiche produit (bouton « Votre soumission en 30 secondes »,
  // demande du propriétaire 2026-10-17) : ?produit=Nom[&qte=N] pré-remplit le
  // 1er article (nom ET quantité choisie sur la fiche) — sans écraser une saisie
  const produitPrefill = useRef(false);
  useEffect(() => {
    if (produitPrefill.current) return;
    produitPrefill.current = true;
    const p = String(params.get("produit") || "").trim().slice(0, 120);
    const q = Math.max(1, Math.min(9999, parseInt(params.get("qte"), 10) || 1));
    if (p) setItems((xs) => xs.map((x, j) => (j === 0 && !x.product_name ? { ...x, product_name: p, qty: q } : x)));
  }, [params]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: k === "phone" ? formatPhone(e.target.value) : e.target.value }));
  const setItem = (i, k, v) => setItems((xs) => xs.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const [uploadingItem, setUploadingItem] = useState(-1);

  // Recherche de compte client pour le PERSONNEL (demande du propriétaire,
  // 2026-10-16) : sur cette page comme au portail vendeur, admin et vendeurs
  // cherchent un compte par nom, prénom, courriel, adresse ou téléphone et le
  // formulaire se remplit tout seul (adresse par défaut du compte d'abord).
  const isStaff = user && (user.role === "admin" || user.role === "vendeur");
  const [clientsStaff, setClientsStaff] = useState([]);
  const [rechercheCompte, setRechercheCompte] = useState("");
  const [choixCompteOuvert, setChoixCompteOuvert] = useState(false);
  const [resultatsCompte, setResultatsCompte] = useState(null);  // recherche serveur (tous les comptes)
  useEffect(() => {
    const t = rechercheCompte.trim();
    if (!isStaff || t.length < 2) { setResultatsCompte(null); return; }
    const h = setTimeout(() => {
      api.get("/vendeur/clients", { params: { q: t } })
        .then(({ data }) => setResultatsCompte(data.clients || []))
        .catch(() => setResultatsCompte([]));
    }, 300);
    return () => clearTimeout(h);
  }, [rechercheCompte, isStaff]);
  useEffect(() => {
    if (!isStaff) return;
    api.get("/vendeur/clients").then(({ data }) => setClientsStaff(data.clients || [])).catch(() => {});
  }, [isStaff]);
  const pickCompte = (c) => {
    const parts = (c.name || "").trim().split(/\s+/);
    const adresse = adressePreferee(c.addresses);
    setForm((f) => ({
      ...f,
      first_name: parts[0] || f.first_name,
      last_name: parts.slice(1).join(" ") || f.last_name,
      organization: c.organization || f.organization,
      email: c.email || f.email,
      phone: c.phone || adresse?.phone || f.phone,
      address1: adresse?.address1 || f.address1,
      city: adresse?.city || c.city || f.city,
      province: adresse?.province || c.province || f.province,
      postal_code: adresse?.postal_code || f.postal_code,
    }));
    setRechercheCompte(`${c.name} — ${c.email}`);
    setChoixCompteOuvert(false);
  };

  // Préremplissage client inscrit (demande du propriétaire 2026-10-09) : nom,
  // courriel, téléphone et adresse complets dès l'ouverture — sans jamais
  // écraser un champ déjà modifié à la main.
  const prefilledRef = useRef(false);
  useEffect(() => {
    if (!user || prefilledRef.current) return;
    prefilledRef.current = true;
    const parts = (user.name || "").trim().split(/\s+/);
    setForm((f) => ({
      ...f,
      first_name: f.first_name || user.first_name || parts[0] || "",
      last_name: f.last_name || user.last_name || parts.slice(1).join(" ") || "",
      email: f.email || user.email || "",
      phone: f.phone || user.phone || "",
      address1: f.address1 || user.address1 || "",
      city: f.city || user.city || "",
      province: f.province || user.province || "",
      postal_code: f.postal_code || user.postal_code || "",
    }));
    // Le carnet du compte complète les champs vides (téléphone/adresse manquants)
    api.get("/account/profile").then(({ data }) => {
      const a0 = (data.addresses || [])[0];
      setForm((f) => ({
        ...f,
        phone: f.phone || data.phone || a0?.phone || "",
        address1: f.address1 || a0?.address1 || "",
        city: f.city || a0?.city || "",
        province: f.province || a0?.province || "",
        postal_code: f.postal_code || a0?.postal_code || "",
      }));
    }).catch(() => {});
  }, [user]);
  // Pièces jointes de l'article (demande du propriétaire) : case « Avec inscription »
  // par article → texte à graver/imprimer + PLUSIEURS fichiers (25 Mo chacun, 10 max)
  const uploadItemLogo = async (i, e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploadingItem(i);
    try {
      for (const file of files) {
        if (file.size > 25 * 1024 * 1024) { toast.error(t("quote.logo_too_big")); continue; }
        if ((items[i].logos || []).length >= 10) { toast.error(t("quote.logo_too_many")); break; }
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads/logo", fd);
        setItems((xs) => xs.map((x, j) => (j === i ? { ...x, logos: [...(x.logos || []), data] } : x)));
      }
      toast.success(t("product.logo_uploaded"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("quote.logo_too_big"));
    } finally {
      setUploadingItem(-1);
    }
  };
  // Pièces jointes GÉNÉRALES de la soumission (demande du propriétaire,
  // 2026-10-27 : « permettre l'envoi de pièces jointes en même temps ») —
  // photos, croquis, exemples… elles partent AVEC la soumission, sans passer
  // par la case « Avec inscription » d'un article
  const [pieces, setPieces] = useState([]);
  const [piecesBusy, setPiecesBusy] = useState(false);
  const uploadPieces = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setPiecesBusy(true);
    try {
      let ajoutes = pieces.length;
      for (const file of files) {
        if (file.size > 25 * 1024 * 1024) { toast.error(t("quote.logo_too_big")); continue; }
        if (ajoutes >= 10) { toast.error(t("quote.pieces_too_many")); break; }
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads/logo", fd);
        setPieces((ps) => [...ps, data]);
        ajoutes += 1;
      }
      toast.success(t("quote.pieces_uploaded"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("quote.logo_too_big"));
    } finally {
      setPiecesBusy(false);
    }
  };

  // Bande déroulante : TOUS les catalogues de la page Les Catalogues (numérotés ou non),
  // PRÉCÉDÉS de « Plaquette sur mesure » en premier choix (demande du propriétaire)
  const catalogues = useMemo(
    () => [
      { value: lang === "en" ? "Custom-made plaque" : "Plaquette sur mesure", no: "" },
      ...(t("catalogues.groups") || []).flatMap((g) =>
        (g.links || []).map((l) => ({ value: l.label, no: catNoOf(l.label) }))),
    ],
    [t, lang]);

  // Arrivée depuis un CATALOGUE ouvert dans la visionneuse (demande du propriétaire,
  // 2026-10-17) : ?catalogue=Nom pré-remplit la bande déroulante du 1er article ;
  // si le nom ne figure pas dans la liste, il va dans le champ « nom de produit »
  const catPrefill = useRef(false);
  useEffect(() => {
    if (catPrefill.current) return;
    catPrefill.current = true;
    const c = String(params.get("catalogue") || "").trim().slice(0, 120);
    if (!c) return;
    const connu = catalogues.some((o) => o.value === c);
    setItems((xs) => xs.map((x, j) => {
      if (j !== 0 || x.catalogue || x.product_name) return x;
      return connu ? { ...x, catalogue: c } : { ...x, product_name: c };
    }));
  }, [params, catalogues]);

  const submit = async (e) => {
    e.preventDefault();
    // Un article est rempli s'il a un catalogue OU un nom de produit hors catalogue
    const kept = items.filter((it) => it.catalogue || String(it.product_name || "").trim());
    if (!kept.length) { toast.error(t("quote.catalogue_required")); return; }
    // Chaque article : nom du produit (hors catalogue) OU Nº de produit — un seul suffit ;
    // avec un Nº de produit, le catalogue devient obligatoire
    if (kept.some((it) => !String(it.product_name || "").trim() && !String(it.product_no || "").trim())) {
      toast.error(t("quote.page_or_product")); return;
    }
    if (kept.some((it) => String(it.product_no || "").trim() && !it.catalogue)) {
      toast.error(t("quote.catalogue_required")); return;
    }
    setLoading(true);
    // Garde-fou client (le serveur refuse aussi) : case texto cochée → contact
    // confirmé par code AVANT l'envoi (cellulaire OU courriel).
    if (prefSmsEtapes && contactConfirm.etape !== "confirme") {
      toast.error(lang === "fr"
        ? "Confirmez votre cellulaire ou votre courriel avec le code à 6 chiffres avant d'envoyer."
        : "Confirm your phone or email with the 6-digit code before sending.");
      setLoading(false);
      return;
    }
    try {
      await api.post("/quick-quote", {
        ...form,
        // Cases cochées → les adresses Facturé à / Livrer à = adresse du dossier client
        bill_address1: sameBill ? form.address1 : form.bill_address1,
        bill_city: sameBill ? form.city : form.bill_city,
        bill_postal_code: sameBill ? form.postal_code : form.bill_postal_code,
        ship_address1: sameShip ? form.address1 : form.ship_address1,
        ship_city: sameShip ? form.city : form.ship_city,
        ship_postal_code: sameShip ? form.postal_code : form.ship_postal_code,
        lang,
        // Source du bouton « Soumission 30 s » cliqué (taux de conversion par endroit)
        source: (() => { try { return sessionStorage.getItem("mt_clic_source") || ""; } catch { return ""; } })(),
        items: kept.map((it) => ({
          catalogue: it.catalogue,
          product_name: String(it.product_name || "").slice(0, 120),
          product_no: String(it.product_no || "").slice(0, 60),
          qty: Math.max(1, parseInt(it.qty, 10) || 1),
          // Case « Avec inscription » (demande du propriétaire) : texte + fichiers 25 Mo
          details: it.with_text ? String(it.details || "").slice(0, 600) : "",
          logo_path: it.with_text && it.logos?.length ? String(it.logos[0].path || "") : "",
          attachments: it.with_text ? (it.logos || []).slice(1, 10).map((l) => String(l.path || "")).filter(Boolean) : [],
        })),
        // Pièces jointes GÉNÉRALES (demande du propriétaire, 2026-10-27) :
        // elles partent EN MÊME TEMPS que la soumission
        attachments: pieces.map((p) => String(p.path || "")).filter(Boolean),
        pref_sms_etapes: prefSmsEtapes,   // case « étapes par texto » (2026-10-30)
      });
      toast.success(t("quote.sent"));
      setForm({ first_name: "", last_name: "", organization: "", address1: "", city: "", province: "", postal_code: "", phone: "", email: "", needed_by: "",
        bill_address1: "", bill_city: "", bill_postal_code: "", ship_address1: "", ship_city: "", ship_postal_code: "" });
      setPrefSmsEtapes(false);
      setContactConfirm({ etape: "idle", via: "texto", code: "", busy: false });
      setSameBill(true);
      setSameShip(true);
      setItems([emptyItem(), emptyItem(), emptyItem()]);
      setPieces([]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="quick-quote-page" className="pt-32 pb-28">
      <SEO title={t("quote.seo_title")} description={t("quote.seo_desc")} path="/soumission-rapide" />
      <div className="max-w-3xl mx-auto px-6">
        <FadeUp>
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3 flex items-center gap-2">
            <Zap className="w-4 h-4" /> {t("quote.kicker")}
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl mb-4">
            {t("quote.title_1")} <span className="text-gold italic block">{t("quote.title_2")}</span>
          </h1>
          <p className="text-ivory/60 text-sm mb-10">{t("quote.intro")}</p>
        </FadeUp>

        <FadeUp delay={0.1}>
          <form onSubmit={submit} className="bg-surface border border-gold/15 rounded-2xl p-6 sm:p-8 space-y-6"
            toolname="demander_soumission"
            tooldescription="Demande une soumission gratuite de trophées, médailles ou plaques personnalisés : coordonnées du client, date de livraison souhaitée, puis pour chaque article le catalogue, le nom ou numéro de produit et la quantité.">
            {isStaff && clientsStaff.length > 0 && (
              <div className="relative border border-gold/25 bg-gold/5 rounded-xl p-4" onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget)) setChoixCompteOuvert(false);
              }}>
                <label htmlFor="qq-client-search" className={labelCls}>
                  {lang === "fr" ? "Compte client (personnel) — nom, courriel, association, adresse ou téléphone" : "Client account (staff) — name, email, association, address or phone"}
                </label>
                <input id="qq-client-search" data-testid="quote-client-search"
                  value={rechercheCompte}
                  onChange={(e) => { setRechercheCompte(e.target.value); setChoixCompteOuvert(true); }}
                  onFocus={() => setChoixCompteOuvert(true)}
                  placeholder={lang === "fr" ? "Chercher un compte client…" : "Search a client account…"}
                  className={inputCls} />
                {choixCompteOuvert && rechercheCompte.trim() && (() => {
                  const resultats = resultatsCompte ?? filtrerClients(clientsStaff, rechercheCompte);
                  return resultats.length > 0 && (
                  <div className="absolute z-20 left-4 right-4 mt-1 bg-ink border border-ivory/15 rounded-lg max-h-56 overflow-y-auto shadow-xl"
                    data-testid="quote-client-resultats">
                    {resultats.map((c) => (
                      <button type="button" key={c.email} data-testid={`quote-client-choix-${c.email}`}
                        onClick={() => pickCompte(c)}
                        className="w-full text-left px-4 py-2.5 text-sm hover:bg-surface transition-colors duration-200 border-b border-ivory/5 last:border-0">
                        <span className="text-ivory">{c.name || "(sans nom)"}</span>
                        <span className="text-ivory/50"> — {c.email}</span>
                        {c.phone && <span className="text-ivory/40"> · {c.phone}</span>}
                      </button>
                    ))}
                  </div>
                  );
                })()}
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="qq-first-name" className={labelCls}>{t("quote.first_name")} *</label>
                <input id="qq-first-name" data-testid="quote-first-name" required maxLength={60}
                  name="prenom" toolparamdescription="Prénom du client"
                  value={form.first_name} onChange={set("first_name")} className={inputCls} />
              </div>
              <div>
                <label htmlFor="qq-last-name" className={labelCls}>{t("quote.last_name")} *</label>
                <input id="qq-last-name" data-testid="quote-last-name" required maxLength={60}
                  name="nom" toolparamdescription="Nom de famille du client"
                  value={form.last_name} onChange={set("last_name")} className={inputCls} />
              </div>
              <div>
                <label htmlFor="qq-org" className={labelCls}>{t("quote.organization")}</label>
                <input id="qq-org" data-testid="quote-organization" maxLength={120}
                  name="organisation" toolparamdescription="Organisation ou entreprise du client (optionnel)"
                  value={form.organization} onChange={set("organization")} className={inputCls} />
              </div>
              {/* ADRESSE RÉORGANISÉE (demande du propriétaire, 2026-11-03) : code
                  postal en premier → ville/province suggérées, puis N° de porte +
                  rue avec autocomplétion (remplit tout automatiquement) */}
              <AddressAutocomplete lang={lang} form={form} setForm={setForm}
                inputCls={inputCls} labelCls={labelCls} PROVINCES={PROVINCES} />
              {/* Facturé à / Livrer à — case à cocher pour copier l'adresse du
                  dossier client (demande du propriétaire, 2026-10-17) ; cochée par
                  défaut, décocher révèle les champs pour une autre adresse */}
              {[
                { titre: t("quote.bill_to"), meme: sameBill, setMeme: setSameBill, prefixe: "bill", tid: "quote-bill" },
                { titre: t("quote.ship_to"), meme: sameShip, setMeme: setSameShip, prefixe: "ship", tid: "quote-ship" },
              ].map(({ titre, meme, setMeme, prefixe, tid }) => (
                <div key={prefixe} className="sm:col-span-1 rounded-xl border border-ivory/15 p-4" data-testid={`${tid}-section`}>
                  <label className="flex items-center gap-2.5 cursor-pointer select-none" data-testid={`${tid}-same-label`}>
                    <input type="checkbox" checked={meme} onChange={(e) => setMeme(e.target.checked)}
                      data-testid={`${tid}-same`}
                      className="w-4 h-4 accent-[#E6C981]" />
                    <span className="text-sm text-ivory/85">
                      <strong className="text-gold">{titre}</strong> — {t("quote.same_as_client")}
                    </span>
                  </label>
                  {!meme && (
                    <div className="mt-3 space-y-3" data-testid={`${tid}-fields`}>
                      <input data-testid={`${tid}-address`} required minLength={3} maxLength={200}
                        placeholder={t("quote.address") + " *"}
                        value={form[`${prefixe}_address1`]} onChange={set(`${prefixe}_address1`)} className={inputCls} />
                      <div className="grid grid-cols-2 gap-3">
                        <input data-testid={`${tid}-city`} required minLength={2} maxLength={100}
                          placeholder={t("quote.city") + " *"}
                          value={form[`${prefixe}_city`]} onChange={set(`${prefixe}_city`)} className={inputCls} />
                        <input data-testid={`${tid}-postal`} required minLength={6} maxLength={7}
                          placeholder={t("quote.postal_code") + " *"}
                          value={form[`${prefixe}_postal_code`]} onChange={set(`${prefixe}_postal_code`)} className={inputCls} />
                      </div>
                    </div>
                  )}
                </div>
              ))}
              <div>
                <label htmlFor="qq-phone" className={labelCls}>{t("quote.phone")} *</label>
                <input id="qq-phone" data-testid="quote-phone" type="tel" required minLength={7} maxLength={30}
                  name="telephone" toolparamdescription="Numéro de téléphone du client"
                  value={form.phone} onChange={set("phone")} className={inputCls} />
                {/* Case « recevoir les étapes par texto » (demande du propriétaire,
                    2026-10-30) — à côté du téléphone ; un code à 6 chiffres confirme.
                    Cochée → confirmation BLOQUANTE du cellulaire OU du courriel
                    (demande 2026-11-03 : « un des 2 doit être confirmé ») */}
                <label className="mt-2 flex items-center gap-2 cursor-pointer select-none w-fit text-xs text-ivory/70 hover:text-emerald-300 transition-colors"
                  data-testid="quote-sms-etapes-case">
                  <input type="checkbox" checked={prefSmsEtapes}
                    onChange={(e) => { setPrefSmsEtapes(e.target.checked); if (!e.target.checked) setContactConfirm({ etape: "idle", via: "texto", code: "", busy: false }); }}
                    className="w-3.5 h-3.5 accent-emerald-500" />
                  {lang === "fr" ? "Recevoir les étapes de ma commande par texto" : "Get my order steps by text"}
                </label>
                {prefSmsEtapes && (
                  <div className="mt-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 space-y-2.5" data-testid="contact-confirm-box">
                    {contactConfirm.etape === "confirme" ? (
                      <p className="text-xs text-emerald-300 font-medium" data-testid="contact-confirm-ok">
                        {lang === "fr" ? "✓ Contact confirmé — vous pouvez envoyer votre soumission." : "✓ Contact confirmed — you can send your request."}
                      </p>
                    ) : (
                      <>
                        <p className="text-[11px] text-ivory/70">
                          {lang === "fr"
                            ? "Pour activer les textos, confirmez votre contact avec un code à 6 chiffres — par texto, ou par courriel si vous n'avez pas votre cellulaire."
                            : "To enable texts, confirm your contact with a 6-digit code — by text, or by email if you don't have your phone."}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <button type="button" onClick={() => envoyerCodeContact("texto")} disabled={contactConfirm.busy}
                            data-testid="contact-code-texto-btn"
                            className="px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-500 text-ink hover:bg-emerald-400 disabled:opacity-50 transition-colors">
                            {lang === "fr" ? "Code par texto" : "Code by text"}
                          </button>
                          <button type="button" onClick={() => envoyerCodeContact("courriel")} disabled={contactConfirm.busy}
                            data-testid="contact-code-courriel-btn"
                            className="px-3 py-1.5 rounded-full text-xs font-semibold border border-emerald-400/40 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50 transition-colors">
                            {lang === "fr" ? "Code par courriel" : "Code by email"}
                          </button>
                        </div>
                        {contactConfirm.etape === "envoye" && (
                          <div className="flex items-center gap-2" data-testid="contact-code-row">
                            <input data-testid="contact-code-input" id="contact-code-input" value={contactConfirm.code} maxLength={6} inputMode="numeric"
                              onChange={(e) => setContactConfirm((c) => ({ ...c, code: e.target.value.replace(/\D/g, "") }))}
                              placeholder={lang === "fr" ? "Code à 6 chiffres" : "6-digit code"}
                              className="w-32 bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm text-center tracking-widest focus:border-emerald-400 focus:outline-none" />
                            <button type="button" onClick={verifierCodeContact} disabled={contactConfirm.busy || contactConfirm.code.length !== 6}
                              data-testid="contact-code-verify"
                              className="px-4 py-2 rounded-full text-xs font-semibold bg-emerald-500 text-ink hover:bg-emerald-400 disabled:opacity-50 transition-colors">
                              {contactConfirm.busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (lang === "fr" ? "Confirmer" : "Confirm")}
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
              <div>
                <label htmlFor="qq-email" className={labelCls}>{t("quote.email")} *</label>
                <input id="qq-email" data-testid="quote-email" type="email" required
                  name="courriel" toolparamdescription="Adresse courriel du client"
                  value={form.email} onChange={set("email")} className={inputCls} />
              </div>
              <div>
                {/* Date limite de livraison OBLIGATOIRE (demande du propriétaire, 2026-10-29) */}
                <label htmlFor="qq-date" className={labelCls}>{t("quote.needed_by")} *</label>
                <input id="qq-date" data-testid="quote-needed-by" type="date" required
                  name="date_livraison" toolparamdescription="Date de livraison souhaitée, format AAAA-MM-JJ (obligatoire)"
                  value={form.needed_by} onChange={set("needed_by")} className={`${inputCls} [color-scheme:dark]`} />
              </div>
            </div>

            <div className="border-t border-ivory/10 pt-6">
              <p className="text-xs uppercase tracking-widest text-gold mb-4" data-testid="quote-items-title">
                {t("quote.items_title")}
              </p>
              <div className="space-y-5">
                {items.map((it, i) => (
                  <div key={i} data-testid={`quote-item-${i}`}
                    className="rounded-xl border border-ivory/10 p-4 relative">
                    <p className="text-[11px] uppercase tracking-widest text-ivory/50 mb-3">
                      {t("quote.choice").replace("{n}", i + 1)}
                    </p>
                    {items.length > 1 && (
                      <button type="button" onClick={() => setItems((xs) => xs.filter((_, j) => j !== i))}
                        data-testid={`quote-item-remove-${i}`} title={t("quote.remove_item")}
                        className="absolute top-3 right-3 text-ivory/40 hover:text-red-400 transition-colors duration-300">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div className="sm:col-span-2">
                        <label htmlFor={`qq-cat-${i}`} className={labelCls}>{t("quote.catalogue")}</label>
                        <select id={`qq-cat-${i}`} data-testid={`quote-item-catalogue-${i}`}
                          name={`article_${i + 1}_catalogue`} toolparamdescription="Catalogue du produit (CAT 1 à CAT 6) — obligatoire si un numéro de produit est inscrit"
                          value={it.catalogue}
                          onChange={(e) => {
                            // « Plaquette sur mesure » → ouvre IMMÉDIATEMENT le formulaire
                            // détaillé dédié (demande du propriétaire)
                            if (["Plaquette sur mesure", "Custom-made plaque"].includes(e.target.value)) {
                              navigate("/plaquettes-sur-mesure");
                              return;
                            }
                            setItem(i, "catalogue", e.target.value);
                          }}
                          className={inputCls}>
                          <option value="">{ts("quote.catalogue_ph")}</option>
                          {catalogues.map((c) => (
                            <option key={c.value} value={c.value}>{c.value}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor={`qq-name-${i}`} className={labelCls}>{t("quote.product_name")} *</label>
                        <input id={`qq-name-${i}`} data-testid={`quote-item-name-${i}`} maxLength={120}
                          name={`article_${i + 1}_nom_produit`} toolparamdescription="Nom du produit (hors catalogue) — sinon inscrire le numéro de produit"
                          value={it.product_name} onChange={(e) => setItem(i, "product_name", e.target.value)}
                          className={inputCls} />
                      </div>
                      <div>
                        <label htmlFor={`qq-prod-${i}`} className={labelCls}>{t("quote.product_no")} *</label>
                        <input id={`qq-prod-${i}`} data-testid={`quote-item-product-${i}`} maxLength={60}
                          name={`article_${i + 1}_no_produit`} toolparamdescription="Numéro du produit dans le catalogue choisi"
                          value={it.product_no} onChange={(e) => setItem(i, "product_no", e.target.value)}
                          className={inputCls} />
                      </div>
                      <div>
                        <label htmlFor={`qq-qty-${i}`} className={labelCls}>{t("quote.qty")}</label>
                        <input id={`qq-qty-${i}`} data-testid={`quote-item-qty-${i}`}
                          type="number" min={1} max={100000} inputMode="numeric"
                          name={`article_${i + 1}_quantite`} toolparamdescription="Quantité désirée (1 et plus)"
                          value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)}
                          className={inputCls} />
                      </div>
                      {/* Case « Avec inscription » (demande du propriétaire) : révèle le
                          texte à graver/imprimer et le téléversement de logo (25 Mo max) */}
                      <div className="sm:col-span-2">
                        <label className="flex items-center gap-2 text-sm text-ivory/80 cursor-pointer select-none w-fit"
                          data-testid={`quote-item-with-text-label-${i}`}>
                          <input type="checkbox" data-testid={`quote-item-with-text-${i}`}
                            checked={!!it.with_text} onChange={(e) => setItem(i, "with_text", e.target.checked)}
                            className="w-4 h-4 accent-[#d4af37] cursor-pointer" />
                          {t("quote.with_text")}
                        </label>
                        {it.with_text && (
                          <div className="mt-3 space-y-3">
                            <div>
                              <label htmlFor={`qq-details-${i}`} className={labelCls}>{t("quote.text_label")}</label>
                              <textarea id={`qq-details-${i}`} data-testid={`quote-item-details-${i}`} rows={2} maxLength={600}
                                name={`article_${i + 1}_inscription`} toolparamdescription="Texte à graver ou imprimer sur l'article (inscription)"
                                placeholder={ts("quote.text_ph")}
                                value={it.details} onChange={(e) => setItem(i, "details", e.target.value)}
                                className={inputCls} />
                            </div>
                            <div>
                              <p className={labelCls}>{t("quote.logo_label")}</p>
                              {(it.logos || []).map((lg, j) => (
                                <div key={j} className="flex items-center gap-3 bg-ink border border-gold/30 rounded-lg px-4 py-2.5 mb-2" data-testid={`quote-item-logo-ok-${i}-${j}`}>
                                  <img src={`${process.env.REACT_APP_BACKEND_URL}${lg.url}`} alt={lg.name} className="w-10 h-10 object-contain bg-white rounded" />
                                  <span className="text-xs text-ivory/70 truncate flex-1">{lg.name}</span>
                                  <button type="button" onClick={() => setItem(i, "logos", (it.logos || []).filter((_, k) => k !== j))} data-testid={`quote-item-logo-remove-${i}-${j}`}
                                    title={t("quote.remove_item")}
                                    className="text-ivory/40 hover:text-red-400 transition-colors duration-300">
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                              {(it.logos || []).length < 10 && (
                                <label className="inline-flex items-center gap-2 border border-dashed border-gold/40 text-gold px-4 py-2.5 rounded-lg text-sm cursor-pointer hover:bg-gold/10 transition-colors duration-300"
                                  data-testid={`quote-item-logo-btn-${i}`}>
                                  {uploadingItem === i ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                                  {t("quote.logo_btn")}
                                  <input type="file" accept=".png,.jpg,.jpeg,.webp,.svg,.pdf,.heic,.heif" multiple className="hidden"
                                    onChange={(e) => uploadItemLogo(i, e)} data-testid={`quote-item-logo-input-${i}`} />
                                </label>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <button type="button"
                onClick={() => setItems((xs) => [...xs, emptyItem()])}
                data-testid="quote-add-item"
                className="mt-4 inline-flex items-center gap-2 border border-gold/40 text-gold px-5 py-2.5 rounded-full text-sm hover:bg-gold hover:text-ink transition-colors duration-300">
                <Plus className="w-4 h-4" /> {t("quote.add_item")}
              </button>
            </div>

            {/* Pièces jointes GÉNÉRALES (demande du propriétaire, 2026-10-27) :
                photos, croquis, exemples… elles partent EN MÊME TEMPS que la
                soumission, sans passer par « Avec inscription » d'un article */}
            <div data-testid="quote-pieces">
              <p className={labelCls}>{t("quote.pieces_label")}</p>
              {pieces.map((p, j) => (
                <div key={j} className="flex items-center gap-3 bg-ink border border-gold/30 rounded-lg px-4 py-2.5 mb-2"
                  data-testid={`quote-piece-ok-${j}`}>
                  <span className="text-xs text-ivory/70 truncate flex-1">{p.name}</span>
                  <button type="button" onClick={() => setPieces((ps) => ps.filter((_, k) => k !== j))}
                    data-testid={`quote-piece-remove-${j}`} title={t("quote.remove_item")}
                    className="text-ivory/40 hover:text-red-400 transition-colors duration-300">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {pieces.length < 10 && (
                <label className="inline-flex items-center gap-2 border border-dashed border-gold/40 text-gold px-4 py-2.5 rounded-lg text-sm cursor-pointer hover:bg-gold/10 transition-colors duration-300"
                  data-testid="quote-pieces-btn">
                  {piecesBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  {t("quote.pieces_btn")}
                  <input type="file" accept=".png,.jpg,.jpeg,.webp,.svg,.pdf,.heic,.heif" multiple className="hidden"
                    onChange={uploadPieces} data-testid="quote-pieces-input" />
                </label>
              )}
              <p className="text-[11px] text-ivory/45 mt-2">{t("quote.pieces_hint")}</p>
            </div>

            {/* Frais de matrice « à déterminer » (demande du propriétaire 2026-10-09) :
                visible sur le formulaire — le montant est confirmé dans la soumission */}
            <p className="text-xs text-gold/90 bg-gold/5 border border-gold/25 rounded-lg px-4 py-3"
              data-testid="quote-matrice-note">
              {t("quote.matrice_note")}
            </p>

            {/* Garde-fou VISUEL (demande du propriétaire, 2026-11-03) : case texto
                cochée → bouton bloqué tant que le contact n'est pas confirmé */}
            {(() => {
              const bloque = prefSmsEtapes && contactConfirm.etape !== "confirme";
              return (
                <>
                  {bloque && (
                    <p className="text-xs text-amber-300 text-center" data-testid="quote-submit-bloque">
                      {lang === "fr"
                        ? "Confirmez votre cellulaire ou votre courriel (code à 6 chiffres) pour activer l'envoi."
                        : "Confirm your phone or email (6-digit code) to enable sending."}
                    </p>
                  )}
                  <button type="submit" disabled={loading || bloque} data-testid="quote-submit"
                    className="w-full inline-flex items-center justify-center gap-2 bg-gold text-ink font-bold px-8 py-4 rounded-full hover:bg-champagne transition-colors duration-300 disabled:opacity-60">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {loading ? t("quote.sending") : t("quote.submit")}
                  </button>
                </>
              );
            })()}
            <p className="text-[11px] text-ivory/45 text-center">{t("quote.hint")}</p>
          </form>
        </FadeUp>
      </div>
    </div>
  );
}
